import { useEffect, useState, useRef } from "react";
import { LocationAutocomplete } from "../ui";
import { LocationIcon, SearchIcon } from "./icons";
import { THEMES } from "./constants";
import SelectChip from "./SelectChip";
import DateRangePicker from "./DateRangePicker";
import CustomStaySection from "./CustomStaySection";
import { trackEvent } from "../../utils/analytics";
import { API_URL } from "../../config";
import {
  destinationsToStops,
  locationToStop,
  stopHasName,
  syncStopsIntoFormData,
} from "../../utils/stopsMigration";

const MAX_TRIP_DAYS = 16;
const MAX_DESTINATIONS = MAX_TRIP_DAYS - 1;

const distributeNights = (stops, totalNights) => {
  if (
    !Array.isArray(stops) ||
    stops.length === 0 ||
    !Number.isFinite(totalNights) ||
    totalNights <= 0
  ) {
    return stops.map((s) => ({ ...s, nights: 0 }));
  }

  const regularStops = stops.filter(s => s.isExplicitDayTrip !== true);

  if (regularStops.length === 0) {
    return stops.map(s => ({ ...s, nights: 0 }));
  }

  const base = Math.floor(totalNights / regularStops.length);
  const remainder = totalNights % regularStops.length;

  let remainderCount = 0;
  return stops.map(s => {
    if (s.isExplicitDayTrip === true) {
      return { ...s, nights: 0 };
    }
    const extra = remainderCount < remainder ? 1 : 0;
    remainderCount++;
    return { ...s, nights: base + extra };
  });
};

const sumNights = (stops) =>
  (stops || []).reduce((s, stop) => s + (parseInt(stop?.nights, 10) || 0), 0);

const Step1Form = ({ formData, setFormData, onOptimizePendingChange }) => {
  const [locationStatus, setLocationStatus] = useState("idle");
  const [addDestInput, setAddDestInput] = useState("");
  const [isAddingDayTrip, setIsAddingDayTrip] = useState(false);
  const [showCustomNights, setShowCustomNights] = useState(false);
  const [duplicateHint, setDuplicateHint] = useState(null);
  const destRef = useRef(null);
  const dateRef = useRef(null);

  const numberOfNights =
    formData.startDate && formData.endDate
      ? Math.max(
        0,
        Math.ceil(
          (new Date(formData.endDate) - new Date(formData.startDate)) /
          (1000 * 60 * 60 * 24),
        ),
      )
      : 0;

  const stops = Array.isArray(formData.stops) ? formData.stops : [];
  const totalAssignedNights = sumNights(stops);

  const detectLocation = async () => {
    if (!navigator.geolocation) {
      setLocationStatus("failed");
      return;
    }
    if (locationStatus === "loading") return;

    if (navigator.permissions) {
      try {
        const perm = await navigator.permissions.query({ name: "geolocation" });
        if (perm.state === "denied") {
          setLocationStatus("denied");
          return;
        }
      } catch {
        // Ignore browsers that don't support the Permissions API
      }
    }

    setLocationStatus("loading");
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        try {
          const response = await fetch(
            `${API_URL}/api/geocode/reverse?lat=${latitude}&lon=${longitude}`,
          );
          if (!response.ok) throw new Error("Geocoding failed");
          const data = await response.json();
          if (data.city) {
            setFormData((prev) => {
              if (prev.startingLocation) return prev;
              return {
                ...prev,
                startingLocation: data.city,
                startingLocationData: {
                  name: data.city,
                  lat: latitude,
                  lon: longitude,
                  state: data.state,
                  country: data.country,
                },
              };
            });
            setLocationStatus("success");
          } else {
            setLocationStatus("failed");
          }
        } catch {
          setLocationStatus("failed");
        }
      },
      (err) => {
        setLocationStatus(err.code === 1 ? "denied" : "failed");
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 },
    );
  };

  useEffect(() => {
    if (!formData.startingLocation) detectLocation();
    if (
      (Array.isArray(formData.stops) && formData.stops.length > 0) ||
      Object.keys(formData.customNightsPerDestination || {}).length > 0
    ) {
      setShowCustomNights(true);
    }
  }, []);

  // One-shot back-fill: if a saved trip arrived with destinations but no stops,
  // derive stops from the legacy fields so the rest of the form can work uniformly.
  useEffect(() => {
    if (!Array.isArray(formData.stops) || formData.stops.length === 0) {
      if ((formData.destinations || []).length > 0) {
        setFormData((prev) => {
          if (Array.isArray(prev.stops) && prev.stops.length > 0) return prev;
          const next = destinationsToStops(
            prev.destinations || [],
            prev.customNightsPerDestination || {},
          );
          return syncStopsIntoFormData(prev, next);
        });
      }
    }
  }, [
    formData.destinations,
    formData.stops,
    formData.customNightsPerDestination,
    setFormData,
  ]);

  useEffect(() => {
    if (stops.length === 0) {
      setAddDestInput("");
      setShowCustomNights(false);
    }
  }, [stops.length]);

  const handleStartingLocationSelect = (location) => {
    if (location) {
      trackEvent("origin_city_selected", {
        city: location.name,
        state: location.state,
        country: location.country,
      });

      setFormData({
        ...formData,
        startingLocation: location.name,
        startingLocationData: location,
      });
      setTimeout(() => {
        destRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 150);
    } else {
      setFormData({
        ...formData,
        startingLocation: "",
        startingLocationData: null,
      });
    }
  };

  const handleAddDestination = (location) => {
    if (!location) return;

    trackEvent("destination_added", {
      destination: location.name,
      state: location.state,
      country: location.country,
      isDayTrip: isAddingDayTrip,
    });

    setFormData((prev) => {
      const prevStops = Array.isArray(prev.stops) ? prev.stops : [];
      if (prevStops.length >= MAX_DESTINATIONS) return prev;

      const isDuplicate = stopHasName(prevStops, location.name);
      if (isDuplicate) {
        setDuplicateHint(location.name);
      } else {
        setDuplicateHint(null);
      }

      const newStop = locationToStop(location, 0);
      if (isAddingDayTrip) {
        newStop.isExplicitDayTrip = true;
      }

      let nextStops = [...prevStops, newStop];
      if (numberOfNights > 0) {
        nextStops = distributeNights(nextStops, numberOfNights);
      }
      return syncStopsIntoFormData(prev, nextStops);
    });

    setAddDestInput("");
    setIsAddingDayTrip(false);
    if (numberOfNights > 0) {
      setShowCustomNights(true);
    } else {
      setShowCustomNights(false);
    }
    setTimeout(() => {
      dateRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 150);
  };

  const handleRemoveDestination = (stopId) => {
    setFormData((prev) => {
      const prevStops = Array.isArray(prev.stops) ? prev.stops : [];
      const removed = prevStops.find((s) => s.id === stopId);
      if (removed) {
        trackEvent("destination_removed", {
          destination: removed.name,
          remaining_destinations: prevStops.length - 1,
        });
      }
      const nextStops = prevStops.filter((s) => s.id !== stopId);
      if (nextStops.length < 2) setShowCustomNights(false);
      if (duplicateHint && !stopHasName(nextStops, duplicateHint)) {
        setDuplicateHint(null);
      }
      return syncStopsIntoFormData(prev, nextStops);
    });
  };

  const toggleCustomNights = () => {
    if (!showCustomNights && stops.length >= 1 && numberOfNights > 0) {
      setFormData((prev) => {
        const next = distributeNights(prev.stops || [], numberOfNights);
        return syncStopsIntoFormData(prev, next);
      });
      setShowCustomNights(true);
    } else {
      setShowCustomNights(false);
    }
  };

  const toggleTheme = (themeId) => {
    const isSelecting = !formData.themes.includes(themeId);

    if (isSelecting) {
      trackEvent("theme_selected", {
        theme: themeId,
      });
    }

    setFormData({
      ...formData,
      themes: formData.themes.includes(themeId)
        ? formData.themes.filter((t) => t !== themeId)
        : [...formData.themes, themeId],
    });
  };

  const nightsLeft = numberOfNights - totalAssignedNights;
  const nightsValid =
    !showCustomNights || totalAssignedNights === numberOfNights;

  const recommendedMaxDest =
    numberOfNights > 0
      ? Math.max(2, Math.ceil(numberOfNights * 0.45))
      : MAX_DESTINATIONS;
  const isOvercrowded = stops.length > recommendedMaxDest && numberOfNights > 0;

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-semibold text-gray-900">
            Where are you starting your trip from?
            <span className="text-red-500 ml-1">*</span>
          </label>
          {locationStatus !== "success" && !formData.startingLocation && (
            <button
              type="button"
              onClick={() => {
                trackEvent("use_location_clicked");
                detectLocation();
              }}
              disabled={locationStatus === "loading"}
              className="flex items-center gap-1.5 text-xs font-medium text-teal-600 hover:text-teal-700 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors"
            >
              {locationStatus === "loading" ? (
                <>
                  <div className="w-3 h-3 border-2 border-teal-600 border-t-transparent rounded-full animate-spin" />
                  Detecting…
                </>
              ) : (
                <>
                  <svg
                    className="w-3.5 h-3.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  Use my location
                </>
              )}
            </button>
          )}
        </div>

        <div className="relative">
          <LocationAutocomplete
            value={formData.startingLocation}
            onChange={(value) =>
              setFormData({ ...formData, startingLocation: value })
            }
            onSelect={handleStartingLocationSelect}
            placeholder={
              locationStatus === "loading"
                ? "Detecting location…"
                : "Enter your city"
            }
            icon={LocationIcon}
            filterType="all"
          />
        </div>

        {locationStatus === "denied" && (
          <p className="mt-1.5 text-xs text-amber-600 flex items-center gap-1">
            <svg
              className="w-3.5 h-3.5 shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
              />
            </svg>
            Location blocked — allow it in your browser's site settings, then
            click "Use my location".
          </p>
        )}
      </div>

      <div ref={destRef}>
        <label className="block text-sm font-semibold text-gray-900 mb-2">
          Where do you want to go?
          <span className="text-red-500 ml-1">*</span>
          <span className="text-gray-400 font-normal ml-2 text-xs">
            (Up to {MAX_DESTINATIONS} cities or countries)
          </span>
        </label>

        {stops.length < MAX_DESTINATIONS ? (
          <div>
            <LocationAutocomplete
              value={addDestInput}
              onChange={setAddDestInput}
              onSelect={handleAddDestination}
              placeholder={
                stops.length === 0
                  ? "Search for a city or country..."
                  : "Add another city or country..."
              }
              icon={SearchIcon}
              filterType="all"
            />
            {addDestInput && stops.length >= 0 && (
              <label className="flex items-center gap-2 mt-2 ml-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isAddingDayTrip}
                  onChange={(e) => setIsAddingDayTrip(e.target.checked)}
                  className="w-4 h-4 text-teal-600 rounded border-gray-300 focus:ring-teal-500"
                />
                <span className="text-sm text-gray-700 font-medium">Add as a Day Trip (0 nights)</span>
              </label>
            )}
          </div>
        ) : (
          <div className="w-full px-4 py-3.5 border border-gray-200 rounded-xl bg-gray-50 text-sm text-gray-400 flex items-center gap-2">
            <SearchIcon />
            <span>Maximum {MAX_DESTINATIONS} destinations reached</span>
          </div>
        )}

        {duplicateHint && (
          <p className="mt-2 text-xs text-teal-700 bg-teal-50 border border-teal-100 rounded-lg px-3 py-2">
            <strong>{duplicateHint}</strong> is already in your trip — adding will be treated as a return visit.
          </p>
        )}

        {stops.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3 items-center">
            {stops.map((stop, index) => (
              <span
                key={stop.id}
                className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 bg-white border border-gray-300 text-gray-900 text-sm font-medium rounded-full shadow-sm"
              >
                <span className="w-4 h-4 bg-teal-600 text-white text-[10px] rounded-full flex items-center justify-center font-bold flex-shrink-0">
                  {index + 1}
                </span>
                {stop.name}
                {stop.country && stop.name !== stop.country && (
                  <span className="text-gray-400 text-xs font-normal">
                    , {stop.country}
                  </span>
                )}
                {stop.isExplicitDayTrip === true || stop.nights === 0 ? (
                  <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full bg-teal-50 border border-teal-200 text-[10px] font-semibold uppercase tracking-wide text-teal-700 shrink-0">
                    Day Trip
                  </span>
                ) : null}
                <button
                  type="button"
                  onClick={() => handleRemoveDestination(stop.id)}
                  aria-label={`Remove ${stop.name}`}
                  className="w-4 h-4 rounded-full bg-gray-100 hover:bg-red-100 text-gray-500 hover:text-red-500 flex items-center justify-center transition-colors flex-shrink-0 text-xs font-bold cursor-pointer"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}

        {isOvercrowded && (
          <div className="mt-3 flex items-start gap-2.5 px-3.5 py-2.5 bg-amber-50 border border-amber-200 rounded-xl">
            <svg
              className="w-4 h-4 text-amber-500 mt-0.5 shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
              />
            </svg>
            <div>
              <p className="text-xs font-semibold text-amber-700">
                Trip may feel rushed
              </p>
              <p className="text-xs text-amber-600 mt-0.5">
                {stops.length} destinations for {numberOfNights} nights —
                recommended max is {recommendedMaxDest}. Consider removing{" "}
                {stops.length - recommendedMaxDest} destination
                {stops.length - recommendedMaxDest > 1 ? "s" : ""} for a more
                relaxed experience.
              </p>
            </div>
          </div>
        )}

        {stops.length >= 1 && numberOfNights > 0 && (
          <div className="mt-3">
            <button
              type="button"
              onClick={toggleCustomNights}
              className="text-sm text-teal-600 hover:text-teal-700 font-medium flex items-center gap-1.5 transition-colors cursor-pointer"
            >
              <svg
                className={`w-3.5 h-3.5 transition-transform ${showCustomNights ? "rotate-180" : ""}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
              {showCustomNights
                ? "Hide custom stay"
                : "Customize nights per city"}
              <span className="text-gray-400 font-normal text-xs">
                (optional — AI auto-splits if skipped)
              </span>
            </button>

            {showCustomNights && (
              <CustomStaySection
                formData={formData}
                setFormData={setFormData}
                numberOfNights={numberOfNights}
                totalAssignedNights={totalAssignedNights}
                nightsLeft={nightsLeft}
                onPendingChange={onOptimizePendingChange}
              />
            )}
          </div>
        )}
      </div>

      <div ref={dateRef}>
        <label className="block text-sm font-semibold text-gray-900 mb-2">
          Select Dates
          <span className="text-red-500 ml-1">*</span>
          <span className="text-gray-400 font-normal ml-2 text-xs">
            (Max {MAX_TRIP_DAYS} days)
          </span>
        </label>
        <DateRangePicker
          startDate={formData.startDate}
          endDate={formData.endDate}
          onStartDateChange={(date) => {
            setFormData((prev) => ({ ...prev, startDate: date }));
          }}
          onEndDateChange={(date) => {
            if (!date || !formData.startDate) return;

            const start = new Date(formData.startDate);
            const end = new Date(date);

            const trip_duration_days = Math.ceil(
              (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24),
            );

            const departure_month = start.toLocaleString("en-US", {
              month: "long",
            });

            const departure_year = start.getFullYear();

            trackEvent("dates_selected", {
              start_date: formData.startDate,
              end_date: date,
              departure_month,
              departure_year,
              trip_duration_days,
            });

            const newNights = Math.max(0, trip_duration_days);

            setFormData((prev) => {
              const prevStops = Array.isArray(prev.stops) ? prev.stops : [];
              let nextStops = prevStops;

              if (prevStops.length >= 1 && newNights > 0 && !showCustomNights) {
                nextStops = distributeNights(prevStops, newNights);
              }

              const synced = syncStopsIntoFormData(prev, nextStops);

              return {
                ...synced,
                endDate: date,
              };
            });

            if (stops.length >= 1 && newNights > 0 && !showCustomNights) {
              setShowCustomNights(true);
            }
          }}
        />
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-1">
          Which of these travel themes best describes your dream getaway?
          <span className="text-gray-400 font-normal ml-2">(Optional)</span>
        </label>
        <div className="flex flex-wrap gap-2 mt-3">
          {THEMES.map((theme) => (
            <SelectChip
              key={theme.id}
              label={theme.label}
              icon={theme.icon}
              selected={formData.themes.includes(theme.id)}
              onClick={() => toggleTheme(theme.id)}
            />
          ))}
        </div>
      </div>

      <input type="hidden" data-nights-valid={nightsValid ? "true" : "false"} />
    </div>
  );
};

export default Step1Form;
